import { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import jobsAPI, { APPLICATION_LOCK_MS, applicationLocked } from '../api/jobs';

const POSITION_CATEGORY = {
  FILTERED: 'filtered',
  SCORED_OFFER: 'scored-offer',
  ADDITIONAL: 'additional'
};
const PAGE_SIZE = 75;
const APP_URL = 'https://www.suitup.website/jobs';

const Jobs = () => {
  const { currentUser } = useAuth();
  const applicantId = currentUser?.applicant_id || currentUser?.resource?.id;
  const applicationStorageKey = `recent-position-applications:${applicantId}`;
  const sharedPositionId = Number(new URLSearchParams(window.location.search).get('position')) || null;
  const [jobTitles, setJobTitles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [sharedPosition, setSharedPosition] = useState(null);
  const [selectedJobTitles, setSelectedJobTitles] = useState([]);
  const [draftJobTitles, setDraftJobTitles] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedPositions, setExpandedPositions] = useState({});
  const [positionDetails, setPositionDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState({});
  const [applyingPositions, setApplyingPositions] = useState(new Set());
  const [sharingPositionId, setSharingPositionId] = useState(null);
  const [applicationTimes, setApplicationTimes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(applicationStorageKey)) || {};
    } catch {
      return {};
    }
  });
  const [hasMorePositions, setHasMorePositions] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(applicationStorageKey, JSON.stringify(applicationTimes));
    } catch {
      // ponytail: fall back to the session lock when browser storage is unavailable.
    }
    const remainingLocks = Object.values(applicationTimes)
      .map(appliedAt => APPLICATION_LOCK_MS - (Date.now() - Number(appliedAt)))
      .filter(remaining => remaining > 0);
    if (!remainingLocks.length) return undefined;
    const timer = setTimeout(() => setApplicationTimes(current => ({ ...current })), Math.min(...remainingLocks));
    return () => clearTimeout(timer);
  }, [applicationStorageKey, applicationTimes]);

  const savedSelection = async () => {
    const response = await jobsAPI.getApplicantJobTitles();
    return (response.data || [])
      .map(preference => preference.job_title?.id || preference.job_title_id || preference.id)
      .filter(Boolean);
  };

  const fetchPositions = async (selection, offset) => {
    const response = await jobsAPI.getPositions(selection, offset, PAGE_SIZE);
    return response.data || {};
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const [titlesResponse, savedIds] = await Promise.all([
          jobsAPI.getJobTitles(),
          savedSelection()
        ]);
        const selection = savedIds;
        const data = await fetchPositions(selection, 0);
        const listedSharedPosition = (data.positions || []).find(position => position.id === sharedPositionId);
        const sharedPositionResponse = sharedPositionId && !listedSharedPosition
          ? await jobsAPI.getPositionDetails(sharedPositionId).catch(() => null)
          : null;
        setJobTitles(titlesResponse.data || []);
        setSelectedJobTitles(selection);
        setDraftJobTitles(selection);
        setPositions(data.positions || []);
        setSharedPosition(listedSharedPosition || sharedPositionResponse?.data || null);
        if (sharedPositionId) setExpandedPositions({ [sharedPositionId]: true });
        setHasMorePositions(data.has_more === true);
      } catch (err) {
        console.error('Error loading positions:', err);
        setError('שגיאה בטעינת המשרות. אנא נסה שנית.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sharedPosition) document.getElementById(`position-${sharedPosition.id}`)?.scrollIntoView({ behavior: 'smooth' });
  }, [sharedPosition]);

  const replacePositions = async (selection) => {
    const data = await fetchPositions(selection, 0);
    setPositions(data.positions || []);
    setHasMorePositions(data.has_more === true);
  };

  const handleJobTitleChange = (jobTitleId) => {
    setDraftJobTitles(current => current.includes(jobTitleId)
      ? current.filter(id => id !== jobTitleId)
      : [...current, jobTitleId]);
  };

  const applyFilters = async () => {
    setIsLoading(true);
    setError('');
    try {
      await replacePositions(draftJobTitles);
      setSelectedJobTitles(draftJobTitles);
      setFiltersOpen(false);
    } catch (err) {
      console.error('Error filtering positions:', err);
      setError('שגיאה בסינון המשרות. אנא נסה שנית.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = async () => {
    setSelectedJobTitles([]);
    setDraftJobTitles([]);
    setIsLoading(true);
    setError('');
    try {
      await replacePositions([]);
    } catch (err) {
      console.error('Error clearing filters:', err);
      setError('שגיאה בניקוי הסינון. אנא נסה שנית.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMorePositions = async () => {
    if (isLoadingMore || !hasMorePositions) return;
    setIsLoadingMore(true);
    setError('');
    try {
      const data = await fetchPositions(selectedJobTitles, positions.length);
      setPositions(current => {
        const known = new Set(current.map(position => position.id));
        return [...current, ...(data.positions || []).filter(position => !known.has(position.id))];
      });
      setHasMorePositions(data.has_more === true);
    } catch (err) {
      console.error('Error loading more positions:', err);
      setError('שגיאה בטעינת משרות נוספות. אנא נסה שנית.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const togglePosition = async (positionId) => {
    const expanding = !expandedPositions[positionId];
    setExpandedPositions(current => ({ ...current, [positionId]: expanding }));
    if (!expanding || positionDetails[positionId] || loadingPositions[positionId]) return;
    setLoadingPositions(current => ({ ...current, [positionId]: true }));
    try {
      const response = await jobsAPI.getPositionDetails(positionId);
      setPositionDetails(current => ({ ...current, [positionId]: response.data }));
    } catch (err) {
      console.error('Error fetching position details:', err);
    } finally {
      setLoadingPositions(current => ({ ...current, [positionId]: false }));
    }
  };

  const handleApply = async (positionId) => {
    if (applyingPositions.has(positionId) || applicationLocked(applicationTimes[positionId])) return;
    setApplyingPositions(current => new Set([...current, positionId]));
    setError('');
    setSuccess('');
    try {
      await jobsAPI.applyToPosition(positionId);
      setApplicationTimes(current => ({ ...current, [positionId]: Date.now() }));
      setSuccess('הגשת מועמדות התקבלה');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error applying to position:', err);
      setSuccess('');
      setError(err.response?.data?.error || 'שגיאה בהגשת המועמדות. אנא נסה שנית.');
    } finally {
      setApplyingPositions(current => {
        const next = new Set(current);
        next.delete(positionId);
        return next;
      });
    }
  };

  const shareUrl = (position) => `${APP_URL}?position=${position.id}`;
  const shareMessage = (position) => [
    'היי, מצאתי ב־Suitup משרה שחשבתי שיכולה להתאים לך:',
    position.title || '',
    '',
    position.company?.public_hidden_name ? `חברה: ${position.company.public_hidden_name}` : null,
    typeof position.location === 'string' ? `מיקום: ${position.location}` : null,
    `קישור לעמוד המשרה: ${shareUrl(position)}`,
    '',
    `עמוד המשרות של Suitup: ${APP_URL}`
  ].filter(line => line !== null).join('\n');
  const emailShareUrl = (position) => `mailto:?${new URLSearchParams({
    subject: 'משרה ב-Suitup',
    body: shareMessage(position).replace(/\n/g, '\r\n')
  }).toString()}`;
  const copyShareLink = async (position) => {
    try {
      await navigator.clipboard.writeText(shareUrl(position));
      setSharingPositionId(null);
      setSuccess('הקישור הועתק');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('שגיאה בהעתקת הקישור. אנא נסה שנית.');
    }
  };

  const listedPositions = positions.filter(position => position.id !== sharedPosition?.id);
  const filteredPositions = listedPositions.filter(position => position.display_category === POSITION_CATEGORY.FILTERED);
  const scoredOfferPositions = listedPositions.filter(position => position.display_category === POSITION_CATEGORY.SCORED_OFFER);
  const additionalPositions = listedPositions.filter(position => position.display_category === POSITION_CATEGORY.ADDITIONAL);

  const renderPosition = (position) => (
    <div id={`position-${position.id}`} key={position.id} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center cursor-pointer flex-1" onClick={() => togglePosition(position.id)}>
          <button className="ml-2 text-gray-500 focus:outline-none" aria-label={expandedPositions[position.id] ? 'כווץ פרטים' : 'הרחב פרטים'}>
            <svg className={`h-5 w-5 transform transition-transform ${expandedPositions[position.id] ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </svg>
          </button>
          <h3 className="text-lg font-medium"><span className="text-yellow-500 text-xl leading-none">⭐&nbsp;</span>{position.title || ''}{position.company?.public_hidden_name ? ` ב${position.company.public_hidden_name}` : ''}</h3>
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          <div>
            <button type="button" onClick={() => setSharingPositionId(position.id)} aria-haspopup="dialog" className="inline-flex items-center justify-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm font-bold text-gray-800 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-500">
              שתף
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4"/></svg>
            </button>
            {sharingPositionId === position.id && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSharingPositionId(null)}>
              <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl sm:p-8" role="dialog" aria-modal="true" aria-labelledby={`share-title-${position.id}`} onClick={event => event.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <h2 id={`share-title-${position.id}`} className="text-2xl font-bold text-slate-800">שיתוף משרה</h2>
                  <button type="button" onClick={() => setSharingPositionId(null)} className="rounded-full p-2 text-4xl font-light leading-none text-gray-500 hover:bg-gray-100" aria-label="סגירת חלון השיתוף">×</button>
                </div>
                <p className="mb-7 mt-5 text-right text-lg text-slate-800">המשרה מתאימה בול לחבר/ה שלך? שתפו את המשרה</p>
                <div className="space-y-3 text-center">
                  <a href={`https://wa.me/?text=${encodeURIComponent(shareMessage(position))}`} target="_blank" rel="noreferrer" onClick={() => setSharingPositionId(null)} className="relative flex min-h-16 items-center justify-center rounded-xl bg-[#4DB75B] px-16 py-4 text-lg font-bold text-white hover:bg-[#43a951]">
                    <svg className="absolute left-6 h-10 w-10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"/></svg>
                    שליחה ב־WhatsApp
                  </a>
                  <button type="button" onClick={() => copyShareLink(position)} className="relative flex min-h-16 w-full items-center justify-center rounded-xl border border-gray-300 px-16 py-4 text-lg font-bold text-slate-800 hover:bg-gray-50">
                    <svg className="absolute left-6 h-9 w-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="m10 13 4-4"/><path d="m7.5 15.5-1 1a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0"/><path d="m16.5 8.5 1-1a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0"/></svg>
                    העתקת קישור
                  </button>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(position))}`} target="_blank" rel="noreferrer" onClick={() => setSharingPositionId(null)} className="relative flex min-h-16 items-center justify-center rounded-xl bg-[#287BE0] px-16 py-4 text-lg font-bold text-white hover:bg-[#226dcc]">
                    <svg className="absolute left-6 h-10 w-10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951"/></svg>
                    שיתוף בפייסבוק
                  </a>
                  <a href={emailShareUrl(position)} onClick={() => setSharingPositionId(null)} className="relative flex min-h-16 items-center justify-center rounded-xl border border-gray-300 px-16 py-4 text-lg font-bold text-slate-800 hover:bg-gray-50">
                    <svg className="absolute left-6 h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M4 6h16v12H4z"/><path d="m4 7 8 6 8-6"/></svg>
                    שליחה במייל
                  </a>
                  <a href={`sms:?body=${encodeURIComponent(shareMessage(position))}`} onClick={() => setSharingPositionId(null)} className="relative flex min-h-16 items-center justify-center rounded-xl border border-gray-300 px-16 py-4 text-lg font-bold text-[#2F9847] hover:bg-gray-50">
                    <svg className="absolute left-6 h-10 w-10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 11.5c0 4.1-4 7.5-9 7.5-1.2 0-2.4-.2-3.4-.6L3 21l1.2-4.1A7 7 0 0 1 2 11.5C2 7.4 6 4 11 4s9 3.4 9 7.5Z"/></svg>
                    שליחה ב־SMS
                  </a>
                </div>
              </div>
            </div>}
          </div>
          <button
            type="button"
            onClick={() => handleApply(position.id)}
            disabled={applyingPositions.has(position.id) || applicationLocked(applicationTimes[position.id])}
            className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${applicationLocked(applicationTimes[position.id]) ? 'border-gray-300 bg-gray-200 text-gray-600 focus:ring-gray-400' : 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100 focus:ring-green-500'}`}
          >
            {applyingPositions.has(position.id) ? 'מגיש...' : applicationLocked(applicationTimes[position.id]) ? 'הוגש' : 'הגש אותי'}
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 10v12M15 5.9 14 10h5.8a2 2 0 0 1 2 2.3l-1 7a2 2 0 0 1-2 1.7H4V10h3l4-8a3 3 0 0 1 4 3.9Z"/></svg>
          </button>
        </div>
      </div>

      {expandedPositions[position.id] && (
        <div className="mt-4 pt-4 border-t border-gray-200 text-right text-sm text-gray-700 space-y-5">
          <div>
            <h4 className="font-semibold mb-2">פרטי המשרה</h4>
            {typeof position.location === 'string' && <div>מיקום: {position.location}</div>}
            <div>תחומים: {(position.job_titles || []).map(jobTitle => jobTitle.name).join(', ')}</div>
          </div>
          {(position.positions_skills || []).length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">כישורים נדרשים</h4>
              {(position.positions_skills || []).map((skill, index) => <div key={skill.skill_id || index}>{skill.name || ''}{skill.importance > 3 ? ' (חובה)' : ''}</div>)}
            </div>
          )}
          {loadingPositions[position.id] && <div className="text-center">טוען פרטי משרה...</div>}
          {positionDetails[position.id]?.outsource_data?.description && <div><h4 className="font-semibold mb-2">תיאור המשרה</h4><div className="whitespace-pre-wrap">{positionDetails[position.id].outsource_data.description}</div></div>}
          {positionDetails[position.id]?.outsource_data?.requirements && <div><h4 className="font-semibold mb-2">דרישות המשרה</h4><div className="whitespace-pre-wrap">{positionDetails[position.id].outsource_data.requirements}</div></div>}
        </div>
      )}
    </div>
  );

  const renderSection = (title, description, items) => items.length > 0 && (
    <div className="space-y-4">
      {(title || description) && <div className="border-t border-white/60 pt-6 first:border-0">
        {title && <h2 className="text-lg font-semibold">{title}</h2>}
        {description && <p className="text-sm text-gray-700 mt-1">{description}</p>}
      </div>}
      {items.map(renderPosition)}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">משרות</h1>
        {error && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 max-w-[calc(100vw-2rem)] rounded-md border border-red-400 bg-red-100 px-5 py-3 text-red-700 shadow-lg" role="alert">{error}</div>}
        {success && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 bg-green-600 text-white px-5 py-3 rounded-md shadow-lg" role="status">{success}</div>}

        <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <button type="button" onClick={() => {
              if (!filtersOpen) setDraftJobTitles(selectedJobTitles);
              setFiltersOpen(current => !current);
            }} className="flex items-center gap-2 text-right" aria-expanded={filtersOpen}>
              <h2 className="text-lg font-semibold">סינון לפי תפקיד</h2>
              <svg className={`h-5 w-5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m7 10 5 5 5-5Z"/></svg>
            </button>
            {filtersOpen && <button type="button" onClick={applyFilters} disabled={isLoading} className="btn-primary shrink-0 disabled:opacity-50">{isLoading ? 'טוען...' : 'הצג משרות'}</button>}
          </div>
          {filtersOpen ? <>
            <div className="mt-4 flex flex-wrap gap-2">
              {jobTitles.map(jobTitle => (
                <label key={jobTitle.id} className={`inline-flex cursor-pointer items-center rounded-full px-3 py-1.5 text-sm font-medium ${draftJobTitles.includes(jobTitle.id) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  <input type="checkbox" className="sr-only" checked={draftJobTitles.includes(jobTitle.id)} onChange={() => handleJobTitleChange(jobTitle.id)} />
                  {jobTitle.name}
                </label>
              ))}
            </div>
          </> : selectedJobTitles.length > 0 && <p className="mt-2 text-sm text-gray-600">{jobTitles.filter(jobTitle => selectedJobTitles.includes(jobTitle.id)).map(jobTitle => jobTitle.name).join(', ')}</p>}
        </div>

        <div className="space-y-6">
          {isLoading && positions.length === 0 ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div> : positions.length > 0 || sharedPosition ? (
            <>
              {sharedPosition && <div className="space-y-4 rounded-lg border-2 border-yellow-400 bg-yellow-50/60 p-3">
                <h2 className="text-lg font-semibold">המשרה ששיתפו איתך</h2>
                {renderPosition(sharedPosition)}
              </div>}
              {renderSection(null, null, filteredPositions)}
              {renderSection('משרות עבורך', null, scoredOfferPositions)}
              {renderSection('משרות נוספות', null, additionalPositions)}
              {hasMorePositions && <div className="flex justify-center mt-6"><button type="button" onClick={loadMorePositions} disabled={isLoadingMore} className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-md disabled:opacity-50">{isLoadingMore ? 'טוען משרות נוספות...' : 'טען עוד משרות'}</button></div>}
            </>
          ) : <div className="bg-white rounded-lg shadow-sm p-8 text-center"><p className="mb-4">אין משרות מתאימות כרגע</p>{selectedJobTitles.length > 0 && <button type="button" onClick={clearFilters} className="btn-primary">צפייה בכל המשרות</button>}</div>}
        </div>
      </div>
    </Layout>
  );
};

export default Jobs;
